import { Button } from '@chakra-ui/button'
import {
  FormControl,
  FormErrorMessage,
  FormLabel
} from '@chakra-ui/form-control'
import {
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay
} from '@chakra-ui/modal'
import { Text as ChakraText, VStack } from '@chakra-ui/react'
import { Select } from '@chakra-ui/select'
import type { FieldProps } from 'formik'
import { Field, Form, Formik } from 'formik'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { Req } from 'use-http'
import useFetch, { CachePolicies } from 'use-http'
import * as Yup from 'yup'
import { colors } from '../../../ui-kit'
import { FeaturesEnum } from '../../../utils/features'
import useToast from '../../hooks/useToast'
import type { UserSessionResponse } from '../../interfaces'
import { EmailChipInput } from './EmailChipInput'

interface Props {
  isOpen: boolean
  user: UserSessionResponse | null
  request: Req
  onClose(): void
}

interface PerformanceSettingsResponse {
  alertFrequency: string
  emails: string[]
}

interface FormProps {
  alertFrequency: string
  emails: string[]
}

const AlertPreferencesModal = ({
  isOpen,
  onClose,
  user,
  request,
}: Props): React.ReactElement => {
  const toast = useToast()
  const { t } = useTranslation()
  const { data: settings, get: getSettingsFetch } =
    useFetch<PerformanceSettingsResponse>({
      cachePolicy: CachePolicies.NO_CACHE,
    })
  const [settingsRefresh, setSettingsRefresh] = useState<boolean>(false)

  useEffect(() => {
    const getSettings = async (): Promise<void> => {
      if (
        user?.pricingPlan.featuresList.includes(FeaturesEnum.PERFORMANCE_CENTER)
      ) {
        await getSettingsFetch(`/api/performance/${user.clientNumber}/settings`)
      }
    }
    void getSettings()
  }, [user, settingsRefresh])

  if (!settings) {
    return <></>
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <Formik<FormProps>
          initialValues={{ alertFrequency: settings.alertFrequency, emails: settings.emails }}
          onSubmit={async (values): Promise<void> => {
            if (user?.clientNumber) {
              await request.post(
                `/api/performance/${user.clientNumber}/settings`,
                { alertFrequency: values.alertFrequency, emails: values.emails },
              )
              if (!request.error) {
                setSettingsRefresh(!settingsRefresh)
              } else {
                toast({
                  description: t(
                    'something_went_wrong',
                    'Something went wrong. Please try again.',
                  ),
                  status: 'error',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
              }
              onClose()
            }
          }}
          validationSchema={Yup.object().shape({
            alertFrequency: Yup.string().required('Required'),
            emails: Yup.array().of(Yup.string().email('Invalid email')),
          })}
        >
          {({ isSubmitting, submitForm, isValid, dirty, values }) => (
            <>
              <ModalHeader>
                {t('alert_preference', 'Alert Preference')}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <Form>
                  <VStack spacing={4} alignItems="flex-start">
                    <Field name="alertFrequency">
                      {({ field, form }: FieldProps<string, FormProps>) => (
                        <FormControl
                          isInvalid={Boolean(
                            form.errors.alertFrequency &&
                            form.touched.alertFrequency,
                          )}
                        >
                          <FormLabel>
                            <ChakraText
                              fontSize="16px"
                              fontWeight="medium"
                              color={colors.appSlate}
                              lineHeight={1.25}
                              marginBottom="0.5em"
                            >
                              Email notification
                              <sup>*</sup>
                            </ChakraText>
                          </FormLabel>
                          <Select {...field}>
                            <option value="7">
                              {t('once_a_week', 'Once a week')}
                            </option>
                            <option value="-1">{t('never', 'Never')}</option>
                          </Select>
                          <FormErrorMessage>
                            {form.errors.alertFrequency}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                    {values.alertFrequency !== '-1' &&
                      <Field name="emails">
                        {({ field, form }: FieldProps<string, FormProps>) => (
                          <FormControl
                            isInvalid={Boolean(
                              form.errors.emails &&
                              form.touched.emails,
                            )}
                          >
                            <FormLabel>
                              <ChakraText
                                fontSize="16px"
                                fontWeight="medium"
                                color={colors.appSlate}
                                lineHeight={1.25}
                                marginBottom="0.5em"
                              >
                                List of recipients
                              </ChakraText>
                            </FormLabel>
                            <EmailChipInput initialEmails={form.initialValues.emails} save={(emails) => {
                              form.setFieldValue('emails', emails)
                            }} />
                            <FormErrorMessage>
                              {form.errors.emails}
                            </FormErrorMessage>
                          </FormControl>
                        )}
                      </Field>
                    }

                  </VStack>
                </Form>
              </ModalBody>
              <ModalFooter borderTop="1px solid #e7eaf0" style={{ gap: '1em' }}>
                <Button onClick={onClose} variant="outline">
                  Cancel
                </Button>
                <Button
                  isDisabled={!isValid || !dirty}
                  type="submit"
                  isLoading={isSubmitting}
                  onClick={() => {
                    void submitForm()
                  }}
                >
                  Submit
                </Button>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ModalContent>
    </Modal>
  )
}

export default AlertPreferencesModal
